import React from 'react';
import { useTranslation } from 'react-i18next';
import type { LinkComponent } from '../../../types/builder';
import { ColorPicker } from '../../Common/ColorPicker';
import { ImageUploader } from '../../Common/ImageUploader';
import { ToggleSwitch } from '../../Common/ToggleSwitch';

interface LinkSettingsProps {
  component: LinkComponent;
  onUpdate: (settings: LinkComponent['settings']) => void;
}

const ANIMATION_OPTIONS = [
  'none',
  'fadeInUp',
  'fadeInDown',
  'slideInLeft',
  'slideInRight',
  'bounce',
  'pulse',
  'scaleUp',
  'rotateIn',
] as const;

export const LinkSettings: React.FC<LinkSettingsProps> = ({ component, onUpdate }) => {
  const { t } = useTranslation('builder');
  const { settings } = component;

  // 🔧 Auto-fix: Add missing fields for old components
  React.useEffect(() => {
    const needsUpdate = !settings.animation || settings.backgroundImage === undefined;
    if (needsUpdate) {
      onUpdate({
        ...settings,
        animation: settings.animation || 'none',
        backgroundImage: settings.backgroundImage ?? '',
      });
    }
  }, []);

  const handleChange = (field: string, value: any) => {
    onUpdate({
      ...settings,
      [field]: value,
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('settings.link.text')}
        </label>
        <input
          type="text"
          value={settings.text}
          onChange={(e) => handleChange('text', e.target.value)}
          placeholder={t('settings.link.textPlaceholder')}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-900"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('settings.link.url')}
        </label>
        <input
          type="text"
          value={settings.url}
          onChange={(e) => handleChange('url', e.target.value)}
          placeholder={t('settings.link.urlPlaceholder')}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-900"
        />
      </div>

      <div>
        <ToggleSwitch
          label={t('settings.common.openInNewTab')}
          checked={settings.openInNewTab}
          onChange={(checked) => handleChange('openInNewTab', checked)}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('settings.link.iconImage')}
        </label>
        <ImageUploader
          currentImage={settings.icon}
          onImageChange={(url) => handleChange('icon', url)}
          aspectRatio="1:1"
        />
        <p className="text-xs text-gray-500 mt-1">
          {t('settings.link.iconHint')}
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('settings.link.iconSize')}
        </label>
        <input
          type="number"
          value={settings.iconSize}
          onChange={(e) => handleChange('iconSize', parseInt(e.target.value))}
          min="32"
          max="128"
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-900"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('settings.link.fontSize')}
        </label>
        <input
          type="number"
          value={settings.fontSize}
          onChange={(e) => handleChange('fontSize', parseInt(e.target.value))}
          min="12"
          max="32"
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-900"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('settings.link.backgroundImage')}
        </label>
        <ImageUploader
          currentImage={settings.backgroundImage || ''}
          onImageChange={(url) => handleChange('backgroundImage', url)}
          aspectRatio="16:9"
        />
        <p className="text-xs text-gray-500 mt-1">
          {t('settings.link.backgroundImageHint')}
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {settings.backgroundImage
            ? t('settings.link.backgroundColorWithImage')
            : t('settings.common.backgroundColor')}
        </label>
        <ColorPicker
          color={settings.backgroundColor}
          onChange={(color) => handleChange('backgroundColor', color)}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('settings.common.hoverBackgroundColor')}
        </label>
        <ColorPicker
          color={settings.hoverBackgroundColor}
          onChange={(color) => handleChange('hoverBackgroundColor', color)}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('settings.common.textColor')}
        </label>
        <ColorPicker
          color={settings.textColor}
          onChange={(color) => handleChange('textColor', color)}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('settings.common.padding')}
        </label>
        <input
          type="range"
          min="0"
          max="50"
          value={settings.padding}
          onChange={(e) => handleChange('padding', parseInt(e.target.value))}
          className="w-full"
        />
        <div className="text-sm text-gray-600 text-center mt-1">{settings.padding}px</div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('settings.link.animationEffect')}
        </label>
        <select
          value={settings.animation}
          onChange={(e) => handleChange('animation', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-900"
        >
          {ANIMATION_OPTIONS.map((key) => (
            <option key={key} value={key}>
              {t(`settings.link.animations.${key}` as const)}
            </option>
          ))}
        </select>
        <p className="text-xs text-gray-500 mt-1">
          {t('settings.link.animationHint')}
        </p>
      </div>
    </div>
  );
};
